import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  ChevronDown, ChevronUp, Eye, FileText, Image as ImageIcon,
  Link as LinkIcon, Video, Calendar, User
} from 'lucide-react';

const UserAnalysesList = ({ analyses, darkMode }) => {
  const [expandedId, setExpandedId] = useState(null);

  const getContentTypeIcon = (type) => {
    switch(type) {
      case 'image': return <ImageIcon className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'url': return <LinkIcon className="w-4 h-4" />;
      case 'text': return <FileText className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="space-y-3">
      {analyses.map((analysis) => (
        <Card 
          key={analysis.id} 
          className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} transition-all`}
        >
          <CardContent className="pt-4">
            {/* Header Row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="flex items-center gap-1">
                  {getContentTypeIcon(analysis.content_type)}
                  {analysis.content_type}
                </Badge>
                
                <Badge 
                  className="capitalize"
                  style={{
                    backgroundColor: analysis.dominant_emotion === 'joy' ? '#10b981' :
                                   analysis.dominant_emotion === 'sadness' ? '#3b82f6' :
                                   analysis.dominant_emotion === 'anger' ? '#ef4444' :
                                   analysis.dominant_emotion === 'fear' ? '#f59e0b' :
                                   '#8b5cf6'
                  }}
                >
                  {analysis.dominant_emotion}
                </Badge>

                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center gap-1`}>
                  <Calendar className="w-3 h-3" />
                  {new Date(analysis.timestamp).toLocaleString()}
                </span>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleExpand(analysis.id)}
                className="flex items-center gap-1"
              >
                {expandedId === analysis.id ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    View Details
                  </>
                )}
              </Button>
            </div>

            {/* Preview */}
            <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
              <span className="font-semibold">Preview: </span>
              {analysis.content_preview?.substring(0, 100)}...
            </div>

            {/* User Info */}
            {analysis.user_id && (
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center gap-1`}>
                <User className="w-3 h-3" />
                User ID: {analysis.user_id.substring(0, 8)}...
              </div>
            )}

            {/* Expanded Details */}
            {expandedId === analysis.id && (
              <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} space-y-4`}>
                {/* Input Content */}
                <div>
                  <div className="font-bold text-sm mb-2 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-blue-600" />
                    Input Content
                  </div>
                  
                  {analysis.content_type === 'image' && analysis.image_data && (
                    <div className="bg-gray-100 dark:bg-gray-900 p-4 rounded-lg">
                      <img 
                        src={analysis.image_data} 
                        alt="Analyzed content"
                        className="max-w-full h-auto max-h-64 object-contain mx-auto rounded"
                      />
                    </div>
                  )}

                  {analysis.content_type === 'text' && (
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap`}>
                        {analysis.content}
                      </p>
                    </div>
                  )}

                  {analysis.content_type === 'url' && (
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
                      <a 
                        href={analysis.content} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        {analysis.content}
                      </a>
                    </div>
                  )}

                  {analysis.content_type === 'video' && (
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Video: {analysis.content}
                      </p>
                    </div>
                  )}
                </div>

                {/* Analysis Results */}
                <div>
                  <div className="font-bold text-sm mb-2">Analysis Results</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                      <div className="text-xs text-green-600 font-semibold">Valence</div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {((analysis.valence || 0) * 100).toFixed(0)}%
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-orange-900/30' : 'bg-orange-50'}`}>
                      <div className="text-xs text-orange-600 font-semibold">Arousal</div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {((analysis.arousal_level || 0) * 100).toFixed(0)}%
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                      <div className="text-xs text-purple-600 font-semibold">Dominance</div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {((analysis.dominance || 0) * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Emotions */}
                {analysis.emotions && (
                  <div>
                    <div className="font-bold text-sm mb-2">Top Emotions</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(analysis.emotions)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 6)
                        .map(([emotion, score]) => (
                          <Badge key={emotion} variant="secondary" className="capitalize">
                            {emotion}: {((score || 0) * 100).toFixed(0)}%
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {analysis.recommendations && analysis.recommendations.length > 0 && (
                  <div>
                    <div className="font-bold text-sm mb-2">Recommendations</div>
                    <div className="space-y-2">
                      {analysis.recommendations.map((rec, idx) => (
                        <div key={idx} className={`p-3 rounded-lg ${darkMode ? 'bg-green-900/30' : 'bg-green-50'} border border-green-300`}>
                          <span className="text-green-700 font-bold mr-2">{idx + 1}.</span>
                          <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audience Reactions */}
                {analysis.audience_reactions && Object.keys(analysis.audience_reactions).length > 0 && (
                  <div>
                    <div className="font-bold text-sm mb-2">Audience Reactions</div>
                    <div className="space-y-2">
                      {Object.entries(analysis.audience_reactions).map(([audience, reaction]) => (
                        <div key={audience} className={`p-3 rounded-lg ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'} border border-purple-300`}>
                          <div className="font-semibold text-purple-700 text-sm mb-1">{audience}</div>
                          <div className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{reaction}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audience Filters */}
                {analysis.audience_filters && (
                  <div>
                    <div className="font-bold text-sm mb-2">Target Audience</div>
                    <div className="flex flex-wrap gap-2">
                      {analysis.audience_filters.region && (
                        <Badge variant="outline">Region: {analysis.audience_filters.region}</Badge>
                      )}
                      {analysis.audience_filters.custom?.country && (
                        <Badge variant="outline">Country: {analysis.audience_filters.custom.country}</Badge>
                      )}
                      {analysis.audience_filters.custom?.gender && (
                        <Badge variant="outline">Gender: {analysis.audience_filters.custom.gender}</Badge>
                      )}
                      {analysis.audience_filters.custom?.age_range && (
                        <Badge variant="outline">Age: {analysis.audience_filters.custom.age_range}</Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default UserAnalysesList;
